import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileContainer, mutationErrorHandler } from "am-tax-fe-core";

import { ROUTE_404, TOAST_DURATION } from "../../utils/constants";

import {
    getClientAsync,
    getclientsAsync,
    getUnlinkedClientsAsync,
    createClientAsync,
    updateClientAsync,
    deleteClientAsync,
    UpdateClient,
    CreateClient,
    uploadIndustryAnalysisFileAsync,
    uploadOrgChartFileAsync,
    getClientIndustryAnalysisFilesAsync,
    deleteIndustryAnalysisDiagramAsync,
} from "./clientApi";
import { queryKeys } from "../queryKeys";
import { useNavigate } from "react-router-dom";
import { isStatusError } from "../../utils/errorHandling";

export function useGetClients() {
    return useQuery({ ...queryKeys.clients.all, queryFn: getclientsAsync });
}

export function useGetUnlinkedClients() {
    return useQuery({
        ...queryKeys.clients.unlinked,
        queryFn: getUnlinkedClientsAsync,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

export const useGetClient = (id: string | undefined) => {
    const toast = useToast();
    const navigate = useNavigate();
    return useQuery({
        ...queryKeys.clients.byId(id || ""),
        queryFn: async () => {
            const client = await getClientAsync(id || "")
                .then(client => {
                    return client;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the client information.",
                        status: "error",
                        isClosable: true,
                    });

                    if (isStatusError(err) && err.status === 404) {
                        navigate(ROUTE_404);
                    }

                    return err;
                });
            return client;
        },
        enabled: !!id,
    });
};

export function useUpdateClient() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: UpdateClient) => {
            return await updateClientAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.clients.all);
            toast({
                title: "Updated",
                description: "Client updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update client"),
    });
}

export function useCreateClient() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: CreateClient) => {
            return await createClientAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.clients.all);
            await queryClient.invalidateQueries(queryKeys.clients.unlinked);
            toast({
                title: "Created",
                description: "Client created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        onError: (error: Response) => {
            if (error.status === 409) {
                toast({
                    title: "Client already exists",
                    description:
                        "This client has already been added to a different user, please reach out to your administrator if you think this isn't correct.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Unable to create client",
                    description: "Please try again in a few minutes and contact support if the problem persists.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        },
    });
}

export function useDeleteClient() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: string) => {
            return await deleteClientAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.clients.all);
            toast({
                title: "Deleted",
                description: "Client deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete client"),
    });
}

export function useUploadOrgDiagram() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer; clientId: string }) => {
            return await uploadOrgChartFileAsync(args.file, args.clientId);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.clients.all);
            toast({
                title: "Uploaded",
                description: "Org chart file uploaded successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to upload org file."),
    });
}

export function useUploadIndustryAnalysisDiagram() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer; clientId: string }) => {
            return await uploadIndustryAnalysisFileAsync(args.file, args.clientId);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.clients.all);
            toast({
                title: "Uploaded",
                description: "Industry analysis file uploaded successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to upload industry analysis file."),
    });
}

export function useGetClientIndustryAnalysisFiles(args: { clientId: string | undefined }) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.clients.byId(args.clientId || ""),
        queryFn: async () => {
            if (args.clientId) {
                return await getClientIndustryAnalysisFilesAsync(args.clientId || "");
            } else {
                return { fileNames: [] };
            }
        },
        enabled: !!args.clientId,
        ...mutationErrorHandler(toast, "Unable to get the industry analysis files for the given client."),
    });
}

export function useDeleteIndustryAnalysisDiagram() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { clientId: string; fileName: string }) => {
            return await deleteIndustryAnalysisDiagramAsync(args.clientId, { blobPath: args.fileName });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.clients.all);
            toast({
                title: "Deleted",
                description: "Industry analysis diagram deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete industry analysis diagram"),
    });
}
